import { JSX, RefObject } from 'preact';
import { useState, useRef, useEffect, useCallback } from 'preact/hooks';
import { Layer } from '../UNSAFE_Layer';
import { Modal } from '../UNSAFE_Modal';
import { FocusTrap } from '../UNSAFE_FocusTrap';
import { focusOn, focusWithin, getActiveElement } from '../utils/PRIVATE_tabbableUtils';
import { normalizePosition } from '../utils/PRIVATE_floatingUtils';
import { RtlSide } from '../UNSAFE_Floating';
import { getClientHints } from '../utils/PRIVATE_clientHints';

// Hooks
import { useOutsideClick } from '../hooks/UNSAFE_useOutsideClick';
import { useAnimation } from '../hooks/UNSAFE_useAnimation';
import { useUser } from '../hooks/UNSAFE_useUser';

// Style imports
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { DrawerPopupVariantOptions } from './themes/DrawerPopupStyles.css';
import { colorInterpolations, ColorProps } from '../utils/UNSAFE_interpolations/colors';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { DrawerPopupRedwoodTheme } from './themes/redwood/DrawerPopupTheme';

// Types
type IntrinsicProps = Pick<JSX.HTMLAttributes<HTMLDivElement>, 'children'>;
type Placement = 'start' | 'end' | 'bottom';
type NormalizedPlacement = 'left' | 'right' | 'bottom';
type Status = 'unmounted' | 'initial' | 'opening' | 'closing';
type VisibilityStatus = 'hidden' | 'visible';
type DisplayModeHorizontal = 'overlay' | 'fullOverlay';
type OnCloseDetail = {
  reason: 'outsideClick' | 'escapeKey';
};

type Props = IntrinsicProps &
  Pick<ColorProps, 'backgroundColor'> & {
    /**
     * Specifies whether the Drawer is open.
     */
    isOpen: boolean;
    /**
     * Specifies at what edge the Drawer opens.
     * Supported values are:
     * <p><code>start</code>, <code>end</code>, <code>bottom</code></p>
     * Default is <code>start</code>.
     */
    placement?: Placement;
    /**
     * Specifies modality of the Darwer.
     * Supported values are:
     * <p><code>modal</code>, <code>modeless</code></p>
     * Default is <code>modal</code>.
     */
    modality?: 'modal' | 'modeless';
    /**
     * Specifies callback triggered when a user tries to close a Drawer through UI interaction.
     * The parent should listen to this event and close the Drawer. If the parent fails to remove
     * the Popup, then no change will be done in the UI by the component.
     * Supported detail values are:
     * <p><code>outsideClick</code>, <code>escapeKey</code></p>
     */
    onClose?: (detail: OnCloseDetail) => void;
    /**
     * Specifies callback triggered after the animation ends.
     */
    onTransitionEnd?: (value: boolean) => void;
    /**
     * Optional ref for an element to focus on when component mounts.
     */
    autoFocusRef?: RefObject<HTMLElement>;
    /**
     * Specifies guidance for screen readers.
     */
    'aria-label'?: string;
    /**
     * Specifies ID of an element used for guidance for screen readers.
     */
    'aria-labelledby'?: string;
    /**
     * Specifies ID of an element (or space separated IDs of multiple elements) that
     * describes the Drawer.
     */
    'aria-describedby'?: string;
  };

// Constants
const displayModefullWidthThreshold = 600;
const zero = '0px';
const duration = 250;

const getElementHeight = (element: HTMLDivElement): number => {
  return Math.round(element.offsetHeight);
};

const getElementWidth = (element: HTMLDivElement): number => {
  return Math.round(element.getBoundingClientRect().width);
};

/**
 * A drawer popup adds a single slide-in side content alongside some primary content to an application window.
 *
 * This drawer always overlays the page and can be placed at the 'start', 'end' or 'bottom' edge.
 */
export const DrawerPopup = ({
  children,
  isOpen = false,
  autoFocusRef,
  placement = 'start',
  modality = 'modal',
  onClose,
  onTransitionEnd,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  ...props
}: Props) => {
  // Initialize states
  const [status, setStatus] = useState<Status>(isOpen ? 'initial' : 'unmounted');
  const [visibility, setVisibility] = useState<VisibilityStatus>('hidden');
  const [isOutsideClickDisabled, setIsOutsideClickDisabled] = useState<boolean>(!isOpen);
  const [displayModeHorizontal, setDisplayModeHorizontal] =
    useState<DisplayModeHorizontal>('overlay');

  const rootRef = useRef<HTMLDivElement>(null);
  const launcherRef = useRef<HTMLElement | null>(null);

  const isBottomPlacement = placement === 'bottom';

  // Animation config
  const animationStates = {
    opening: () => {
      if (isBottomPlacement) {
        return {
          from: {
            translateY: getTranslateY()
          },
          to: {
            translateY: zero
          },
          options: {
            duration: duration
          }
        };
      } else {
        return {
          from: {
            translateX: getTranslateX()
          },
          to: {
            translateX: zero
          },
          options: {
            duration: duration
          }
        };
      }
    },
    closing: () => {
      if (isBottomPlacement) {
        return {
          from: {
            translateY: zero
          },
          to: {
            translateY: getTranslateY()
          },
          options: {
            duration: duration
          }
        };
      } else {
        return {
          from: {
            translateX: zero
          },
          to: {
            translateX: getTranslateX()
          },
          options: {
            duration: duration
          }
        };
      }
    }
  };

  // Normalizing position
  const { direction } = useUser();
  const getNormalizedPlacement = (): RtlSide => {
    return normalizePosition(placement, direction);
  };

  const getTranslateX = (): string => {
    return `${getNormalizedPlacement() === 'right' ? '' : '-'}${getElementWidth(
      rootRef.current as HTMLDivElement
    )}px`;
  };

  const getTranslateY = (): string => {
    return `${getElementHeight(rootRef.current as HTMLDivElement)}px`;
  };

  // Animation
  const { nodeRef } = useAnimation<Status, HTMLDivElement>(status, {
    animationStates,
    isAnimatedOnMount: true,
    onAnimationEnd: useCallback(() => {
      if (status === 'opening') {
        onTransitionEnd?.(true);
      } else if (!isOpen) {
        nodeRef(null);
        setVisibility('hidden');
        setStatus('unmounted');
        onTransitionEnd?.(false);
      }
    }, [status, isOpen])
  });

  useEffect(() => {
    // 1. Ignore status update on initial render
    if (status === 'unmounted' && !isOpen) {
      return;
    }

    // 2. Mount visually hidden component
    // Component gets mounted, but will be visually hidden to avoid flickering
    // Flickering happens because it gets rendered before animation hook repositions
    // it in next render cycle
    if (status === 'unmounted' && isOpen) {
      setStatus('initial');
      return;
    }

    // 3. Setup animation
    // Component has been mounted.
    // We will kick out animation as node is already present in DOM.
    // To avoid flickering, we still keep it visually hidden until the next render cycle.
    if (status === 'initial' && isOpen) {
      nodeRef(rootRef.current);
      setIsOutsideClickDisabled(false);
      setStatus('opening');
      return;
    }

    // 3. Unhide component. It becomes visible.
    if (status === 'opening' && isOpen) {
      setVisibility('visible');
      return;
    }

    // 4. Closing
    if (!isOpen) {
      setIsOutsideClickDisabled(true);
      setStatus('closing');
    }
  }, [isOpen, status]);

  // Launcher handler
  useEffect(() => {
    if (status === 'opening') {
      // Remember launcher
      const activeElement = getActiveElement();
      launcherRef.current = activeElement === document.body ? document.body : activeElement;

      // Register F6 key handler to enter the Popup
      launcherRef.current?.addEventListener('keydown', launcherKeyDownCallback);
    }
    if (status === 'closing') {
      return () => {
        // Deregister F6 key handler
        launcherRef.current?.removeEventListener('keydown', launcherKeyDownCallback);
      };
    }
    return;
  }, [status]);

  const launcherKeyDownCallback = (event: KeyboardEvent): void => {
    // Moves focus from launcher (must have focus) back to the Drawer
    if (launcherRef.current === getActiveElement() && event.code === 'F6') {
      // Prevent default F6 handlers.
      // F6 is a standard Chrome address bar shortcut on Windows.
      event.preventDefault();
      event.stopPropagation();

      // Focus on first tabbable in a container or container itself if there is none
      focusWithin(rootRef.current as HTMLElement);
    }
  };

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const launcherEl = launcherRef.current;
      switch (event.code) {
        case 'Escape': {
          onClose?.({ reason: 'escapeKey' });
          break;
        }
        case 'F6':
          // Prevent default F6 handlers.
          // F6 is a standard Chrome address bar shortcut on Windows.
          event.preventDefault();
          event.stopPropagation();
          // Focus launcher
          if (launcherEl) {
            focusOn(launcherEl);
          }
          break;
      }
    },
    [onClose]
  );

  // OutsideClick handler
  const handleOutsideClick = useCallback(() => {
    // Trigger onClose event with outside click detail
    onClose?.({ reason: 'outsideClick' });
  }, [onClose]);

  useOutsideClick({
    isDisabled: isOutsideClickDisabled,
    ref: rootRef,
    handler: handleOutsideClick
  });

  // Drawer placement
  const getDrawerPlacement = (): NormalizedPlacement => {
    if (isBottomPlacement) {
      return placement;
    } else {
      if (direction === 'rtl') {
        return placement === 'start' ? 'right' : 'left';
      } else {
        return placement === 'start' ? 'left' : 'right';
      }
    }
  };

  // Window resize handler
  // Handles display mode: Overlay <> Full overlay (full width side drawers)
  const handleResize = () => {
    const viewportWidth = getViewportWidth();
    if (displayModeHorizontal === 'overlay' && viewportWidth < displayModefullWidthThreshold) {
      setDisplayModeHorizontal('fullOverlay');
    }
    if (displayModeHorizontal === 'fullOverlay' && viewportWidth > displayModefullWidthThreshold) {
      setDisplayModeHorizontal('overlay');
    }
  };

  const clientHints = getClientHints();
  const getViewportWidth = (): number => {
    if (clientHints.platform === 'ios') {
      // On ios window.innerWidth is not recommended way of measuring the viewport
      return document.documentElement.clientWidth;
    }
    return window.innerWidth;
  };

  useEffect(() => {
    const root = rootRef.current;
    if (root) {
      const resizeObserver = new ResizeObserver(() => {
        handleResize();
      });
      resizeObserver.observe(document.body);
      return () => {
        resizeObserver.unobserve(document.body);
      };
    }
    return undefined;
  }, [handleResize]);

  // Styles
  const { classes } = useComponentTheme<DrawerPopupVariantOptions>(DrawerPopupRedwoodTheme, {
    placement: getDrawerPlacement(),
    visibility,
    displayModeHorizontal
  });

  const interpolations = [...Object.values(colorInterpolations)];
  const styleInterpolations = mergeInterpolations<ColorProps>(interpolations);
  const { ...styles } = styleInterpolations(props);

  const renderDrawer = () => {
    return (
      <div
        ref={rootRef}
        className={classes}
        style={styles}
        tabIndex={-1}
        role="dialog"
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel}
        aria-labeledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}>
        <FocusTrap autoFocusRef={autoFocusRef}>{children}</FocusTrap>
      </div>
    );
  };

  const isMounted = status !== 'unmounted';
  if (isMounted) {
    // Modal vs. modelss upper wrapper
    if (modality === 'modal') {
      return <Modal isOpen={isMounted}>{renderDrawer()}</Modal>;
    } else {
      return <Layer>{renderDrawer()}</Layer>;
    }
  }

  return null;
};
